import {
  ScheduledQueryDay,
  ScheduledQueryFrequency,
  ScheduledQueryOccurrence,
  ScheduledQueryWindow,
} from '@api/api/scheduled-queries';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import styles from './ScheduleOptionsRow.module.scss';
import { ScheduleSelectField } from './ScheduleSelectField';

export type ScheduleFrequency = ScheduledQueryFrequency | 'monthlyByDate';

const FREQUENCY_OPTIONS: ScheduleFrequency[] = ['daily', 'weekly', 'monthly'];
const FREQUENCY_OPTIONS_WITH_DATE: ScheduleFrequency[] = [
  ...FREQUENCY_OPTIONS,
  'monthlyByDate',
];
const DAY_OPTIONS: ScheduledQueryDay[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];
const OCCURRENCE_OPTIONS: ScheduledQueryOccurrence[] = [
  'first',
  'second',
  'third',
  'fourth',
  'last',
];
const DAY_OF_MONTH_OPTIONS = Array.from({ length: 31 }, (_, i) =>
  String(i + 1)
);
const DELIVERY_TIME_OPTIONS: ScheduledQueryWindow[] = [
  'morning',
  'afternoon',
  'evening',
];

// Must match the scheduler's SCHEDULE_WINDOW_* env values in rflx.
const DELIVERY_TIME_RANGE_HOURS: Record<
  ScheduledQueryWindow,
  [number, number]
> = {
  morning: [6, 9],
  afternoon: [13, 16],
  evening: [17, 20],
};

const formatDeliveryTimeRange = ([startHour, endHour]: [number, number]) =>
  new Intl.DateTimeFormat(navigator.language, {
    hour: 'numeric',
    minute: '2-digit',
  }).formatRange(
    new Date(2000, 0, 1, startHour),
    new Date(2000, 0, 1, endHour)
  );

export interface ScheduleOptionsValue {
  frequency: ScheduleFrequency;
  day: ScheduledQueryDay;
  occurrence: ScheduledQueryOccurrence;
  dayOfMonth: number;
  deliveryTime: ScheduledQueryWindow;
}

type ScheduleOptionsChangeHandler = {
  (field: 'frequency', value: ScheduleFrequency): void;
  (field: 'day', value: ScheduledQueryDay): void;
  (field: 'occurrence', value: ScheduledQueryOccurrence): void;
  (field: 'dayOfMonth', value: number): void;
  (field: 'deliveryTime', value: ScheduledQueryWindow): void;
};

export interface ScheduleOptionsRowProps {
  value: ScheduleOptionsValue;
  onChange: ScheduleOptionsChangeHandler;
}

function resolveOption<T extends string>(
  options: T[],
  raw: string
): T | undefined {
  return options.find(o => o === raw);
}

export const ScheduleOptionsRow = ({
  value,
  onChange,
}: ScheduleOptionsRowProps) => {
  const { t } = useTranslation(['chat']);
  const isTasksUpdate = useFlags(state =>
    state.getFeatureFlagState('REASON-2703-scheduled-tasks-update')
  );

  const frequencyOptions = isTasksUpdate
    ? FREQUENCY_OPTIONS_WITH_DATE
    : FREQUENCY_OPTIONS;

  const getTranslatedLabel = (keyPrefix: string) => (option: string) =>
    t(`${keyPrefix}.${option}`);

  const getDeliveryTimeLabel = (option: string) => {
    const name = t(`chat:scheduledQuerySettings.deliveryTimeOptions.${option}`);
    const deliveryWindow = resolveOption(DELIVERY_TIME_OPTIONS, option);

    return deliveryWindow
      ? t('chat:scheduledQuerySettings.deliveryTimeWithRange', {
          name,
          range: formatDeliveryTimeRange(
            DELIVERY_TIME_RANGE_HOURS[deliveryWindow]
          ),
        })
      : name;
  };

  const handleFrequencyChange = (raw: string) => {
    const freq = resolveOption(frequencyOptions, raw);
    if (!freq) {
      return;
    }
    onChange('frequency', freq);
    if (freq === 'monthly') {
      onChange('occurrence', 'first');
    }
  };

  return (
    <div
      className={clsx(
        styles.scheduleRow,
        isTasksUpdate && styles.scheduleRowWithDeliveryRange
      )}
    >
      <ScheduleSelectField
        label={t('chat:scheduledQuerySettings.frequency')}
        options={frequencyOptions}
        value={value.frequency}
        getOptionLabel={getTranslatedLabel(
          'chat:scheduledQuerySettings.frequencyOptions'
        )}
        testId="scheduled-query-frequency"
        onChange={handleFrequencyChange}
      />

      {value.frequency === 'weekly' && (
        <ScheduleSelectField
          label={t('chat:scheduledQuerySettings.day')}
          options={DAY_OPTIONS}
          value={value.day}
          getOptionLabel={getTranslatedLabel(
            'chat:scheduledQuerySettings.dayOptions'
          )}
          testId="scheduled-query-day"
          onChange={raw => {
            const day = resolveOption(DAY_OPTIONS, raw);
            if (day) {
              onChange('day', day);
            }
          }}
        />
      )}

      {value.frequency === 'monthly' && (
        <>
          <ScheduleSelectField
            label={t('chat:scheduledQuerySettings.occurrence')}
            options={OCCURRENCE_OPTIONS}
            value={value.occurrence}
            getOptionLabel={getTranslatedLabel(
              'chat:scheduledQuerySettings.occurrenceOptions'
            )}
            testId="scheduled-query-occurrence"
            onChange={raw => {
              const occ = resolveOption(OCCURRENCE_OPTIONS, raw);
              if (occ) {
                onChange('occurrence', occ);
              }
            }}
          />
          <ScheduleSelectField
            label={t('chat:scheduledQuerySettings.day')}
            options={DAY_OPTIONS}
            value={value.day}
            getOptionLabel={getTranslatedLabel(
              'chat:scheduledQuerySettings.dayOptions'
            )}
            testId="scheduled-query-day-monthly"
            onChange={raw => {
              const day = resolveOption(DAY_OPTIONS, raw);
              if (day) {
                onChange('day', day);
              }
            }}
          />
        </>
      )}

      {value.frequency === 'monthlyByDate' && (
        <ScheduleSelectField
          label={t('chat:scheduledQuerySettings.dayOfMonth')}
          options={DAY_OF_MONTH_OPTIONS}
          value={String(value.dayOfMonth)}
          testId="scheduled-query-day-of-month"
          getOptionLabel={option => option}
          onChange={raw => {
            const dayOfMonth = resolveOption(DAY_OF_MONTH_OPTIONS, raw);
            if (dayOfMonth) {
              onChange('dayOfMonth', Number(dayOfMonth));
            }
          }}
        />
      )}

      <ScheduleSelectField
        label={t('chat:scheduledQuerySettings.deliveryTime')}
        options={DELIVERY_TIME_OPTIONS}
        value={value.deliveryTime}
        testId="scheduled-query-delivery-time"
        getOptionLabel={
          isTasksUpdate
            ? getDeliveryTimeLabel
            : getTranslatedLabel(
                'chat:scheduledQuerySettings.deliveryTimeOptions'
              )
        }
        onChange={raw => {
          const time = resolveOption(DELIVERY_TIME_OPTIONS, raw);
          if (time) {
            onChange('deliveryTime', time);
          }
        }}
      />
    </div>
  );
};
